import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import {
  useAlertCategories,
  useChannels,
  useCreateChannel,
  useCreateRole,
  useDeleteChannel,
  useMySubscriptions,
  useRoles,
  useTestChannel,
  useUpdateSubscriptions,
} from "@/hooks/use-alerts";
import type { NotificationChannel, UserSubscription } from "@/lib/types";
import { Bell, Hash, Mail, Plus, Send, Trash2 } from "lucide-react";
import { useState } from "react";

// ─── Shared styles ───────────────────────────────────────────────────────────

const inputCls = "iris-input w-full";
const selectCls = "iris-input w-full" + " cursor-pointer";

// ─── Channel icon ─────────────────────────────────────────────────────────────

function ChannelIcon({ kind }: { kind: NotificationChannel["kind"] }) {
  if (kind === "email") return <Mail size={16} style={{ color: "var(--iris-neon-purple)" }} />;
  if (kind === "slack_webhook")
    return <Hash size={16} style={{ color: "var(--iris-neon-green)" }} />;
  return <Bell size={16} style={{ color: "var(--iris-neon-orange)" }} />;
}

// ─── Add Channel Dialog ───────────────────────────────────────────────────────

type ChannelForm = {
  kind: NotificationChannel["kind"];
  name: string;
  from_address: string;
  webhook_url: string;
};

const EMPTY_CHANNEL: ChannelForm = {
  kind: "email",
  name: "",
  from_address: "",
  webhook_url: "",
};

function AddChannelDialog({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState<ChannelForm>(EMPTY_CHANNEL);
  const createChannel = useCreateChannel();

  function handleSubmit() {
    const config: Record<string, string> = {};
    if (form.kind === "email") config.from_address = form.from_address;
    if (form.kind === "slack_webhook") config.webhook_url = form.webhook_url;

    createChannel.mutate(
      { kind: form.kind, name: form.name, config, enabled: true },
      { onSuccess: onClose },
    );
  }

  return (
    <DialogContent>
      <DialogTitle>Add Channel</DialogTitle>
      <DialogDescription>Configure a new notification delivery channel.</DialogDescription>

      <div className="flex flex-col gap-3 pt-1">
        <div className="flex flex-col gap-1">
          <label htmlFor="ch-kind" className="iris-meta">
            Type
          </label>
          <select
            id="ch-kind"
            value={form.kind}
            onChange={(e) =>
              setForm({ ...form, kind: e.target.value as NotificationChannel["kind"] })
            }
            className={selectCls}
          >
            <option value="email">Email</option>
            <option value="slack_webhook">Slack Webhook</option>
            <option value="in_app">In-App</option>
          </select>
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="ch-name" className="iris-meta">
            Name
          </label>
          <input
            id="ch-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Ops Slack Channel"
            className={inputCls}
          />
        </div>

        {form.kind === "email" && (
          <div className="flex flex-col gap-1">
            <label htmlFor="ch-from" className="iris-meta">
              From Address
            </label>
            <input
              id="ch-from"
              value={form.from_address}
              onChange={(e) => setForm({ ...form, from_address: e.target.value })}
              placeholder="alerts@example.com"
              className={inputCls}
            />
          </div>
        )}

        {form.kind === "slack_webhook" && (
          <div className="flex flex-col gap-1">
            <label htmlFor="ch-webhook" className="iris-meta">
              Webhook URL
            </label>
            <input
              id="ch-webhook"
              value={form.webhook_url}
              onChange={(e) => setForm({ ...form, webhook_url: e.target.value })}
              placeholder="https://hooks.slack.com/services/..."
              className={inputCls}
            />
          </div>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="iris-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="iris-btn iris-btn--primary"
            disabled={!form.name || createChannel.isPending}
            onClick={handleSubmit}
          >
            {createChannel.isPending ? "Saving\u2026" : "Add Channel"}
          </button>
        </div>
      </div>
    </DialogContent>
  );
}

// ─── Channels Section ─────────────────────────────────────────────────────────

function ChannelsSection() {
  const { data: channels, isLoading } = useChannels();
  const deleteChannel = useDeleteChannel();
  const testChannel = useTestChannel();
  const [showAdd, setShowAdd] = useState(false);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 style={{ fontSize: 13, fontWeight: 600, color: "var(--iris-fg-1)" }}>
            Notification Channels
          </h2>
          <p className="iris-meta">Delivery destinations for alerts</p>
        </div>
        <button type="button" className="iris-btn" onClick={() => setShowAdd(true)}>
          <Plus size={13} />
          Add Channel
        </button>
      </div>

      {isLoading ? (
        <p style={{ fontSize: 12, color: "var(--iris-fg-3)" }}>Loading\u2026</p>
      ) : (
        <div className="grid grid-cols-3 gap-3">
          {(channels ?? []).map((ch: NotificationChannel) => (
            <div key={ch.id} className="iris-card flex flex-col gap-3" style={{ padding: 16 }}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2">
                  <ChannelIcon kind={ch.kind} />
                  <span style={{ fontSize: 12, fontWeight: 500, color: "var(--iris-fg-1)" }}>
                    {ch.name}
                  </span>
                </div>
                <span
                  className="rounded-full px-2 py-0.5"
                  style={{
                    fontSize: 10,
                    fontWeight: 500,
                    background: ch.enabled ? "rgba(57,255,136,0.1)" : "rgba(255,255,255,0.04)",
                    color: ch.enabled ? "var(--iris-neon-green)" : "var(--iris-fg-4)",
                  }}
                >
                  {ch.enabled ? "enabled" : "disabled"}
                </span>
              </div>

              <p className="iris-meta">
                {ch.kind === "email" && "Email"}
                {ch.kind === "slack_webhook" && "Slack Webhook"}
                {ch.kind === "in_app" && "In-App"}
              </p>

              <div className="flex gap-2 pt-1">
                <button
                  type="button"
                  className="iris-btn"
                  disabled={testChannel.isPending}
                  onClick={() => testChannel.mutate(ch.id)}
                  title="Send test notification"
                >
                  <Send size={12} />
                  Test
                </button>
                <button
                  type="button"
                  className="iris-btn"
                  disabled={deleteChannel.isPending}
                  onClick={() => deleteChannel.mutate(ch.id)}
                  title="Delete channel"
                  style={{ color: "var(--iris-neon-pink)" }}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          ))}

          {(channels ?? []).length === 0 && (
            <div
              className="iris-card col-span-3"
              style={{ padding: "16px 18px", fontSize: 12, color: "var(--iris-fg-3)" }}
            >
              No channels configured
            </div>
          )}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        {showAdd && <AddChannelDialog onClose={() => setShowAdd(false)} />}
      </Dialog>
    </section>
  );
}

// ─── Add Role Dialog ──────────────────────────────────────────────────────────

type RoleForm = { name: string; description: string };
const EMPTY_ROLE: RoleForm = { name: "", description: "" };

function AddRoleDialog({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState<RoleForm>(EMPTY_ROLE);
  const createRole = useCreateRole();

  function handleSubmit() {
    createRole.mutate(form, { onSuccess: onClose });
  }

  return (
    <DialogContent>
      <DialogTitle>Add Role</DialogTitle>
      <DialogDescription>Create a recipient role to group notification targets.</DialogDescription>

      <div className="flex flex-col gap-3 pt-1">
        <div className="flex flex-col gap-1">
          <label htmlFor="role-name" className="iris-meta">
            Name
          </label>
          <input
            id="role-name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. Ops Team"
            className={inputCls}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label htmlFor="role-desc" className="iris-meta">
            Description
          </label>
          <input
            id="role-desc"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="Brief description of this role"
            className={inputCls}
          />
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className="iris-btn" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="iris-btn iris-btn--primary"
            disabled={!form.name || createRole.isPending}
            onClick={handleSubmit}
          >
            {createRole.isPending ? "Saving\u2026" : "Add Role"}
          </button>
        </div>
      </div>
    </DialogContent>
  );
}

// ─── Roles Section ────────────────────────────────────────────────────────────

function RolesSection() {
  const { data: roles, isLoading } = useRoles();
  const [showAdd, setShowAdd] = useState(false);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 style={{ fontSize: 13, fontWeight: 600, color: "var(--iris-fg-1)" }}>
            Recipient Roles
          </h2>
          <p className="iris-meta">Groups of users that receive alert notifications</p>
        </div>
        <button type="button" className="iris-btn" onClick={() => setShowAdd(true)}>
          <Plus size={13} />
          Add Role
        </button>
      </div>

      {isLoading ? (
        <p style={{ fontSize: 12, color: "var(--iris-fg-3)" }}>Loading\u2026</p>
      ) : (
        <div className="iris-card" style={{ overflow: "hidden", padding: 0 }}>
          {(roles ?? []).map((role, idx) => (
            <div
              key={role.id}
              className="flex items-center gap-4"
              style={{
                padding: "12px 18px",
                fontSize: 12,
                borderBottom:
                  idx < (roles ?? []).length - 1 ? "1px solid var(--iris-line-1)" : undefined,
              }}
            >
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <span style={{ fontWeight: 500, color: "var(--iris-fg-1)" }}>{role.name}</span>
                {role.description && <span className="iris-meta">{role.description}</span>}
              </div>
              <span
                className="shrink-0 rounded-full px-2 py-0.5"
                style={{
                  fontSize: 10,
                  fontWeight: 500,
                  background: "rgba(168,85,247,0.1)",
                  color: "var(--iris-neon-purple)",
                }}
              >
                {role.member_count} {role.member_count === 1 ? "member" : "members"}
              </span>
            </div>
          ))}

          {(roles ?? []).length === 0 && (
            <div style={{ padding: "16px 18px", fontSize: 12, color: "var(--iris-fg-3)" }}>
              No roles configured
            </div>
          )}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        {showAdd && <AddRoleDialog onClose={() => setShowAdd(false)} />}
      </Dialog>
    </section>
  );
}

// ─── My Preferences Section ───────────────────────────────────────────────────

function PreferencesSection() {
  const { data: subscriptions, isLoading: subsLoading } = useMySubscriptions();
  const { data: categories, isLoading: catLoading } = useAlertCategories();
  const updateSubscriptions = useUpdateSubscriptions();

  // Local edits keyed by subscription id
  const [edits, setEdits] = useState<Record<number, Partial<UserSubscription>>>({});

  const isLoading = subsLoading || catLoading;

  function patch(id: number, delta: Partial<UserSubscription>) {
    setEdits((prev) => ({ ...prev, [id]: { ...prev[id], ...delta } }));
  }

  function getField<K extends keyof UserSubscription>(sub: UserSubscription, key: K) {
    return (edits[sub.id]?.[key] ?? sub[key]) as UserSubscription[K];
  }

  function handleSave() {
    const payload = Object.entries(edits).map(([id, delta]) => ({ id: Number(id), ...delta }));
    updateSubscriptions.mutate(payload, { onSuccess: () => setEdits({}) });
  }

  const isDirty = Object.keys(edits).length > 0;

  return (
    <section className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div>
          <h2 style={{ fontSize: 13, fontWeight: 600, color: "var(--iris-fg-1)" }}>
            My Preferences
          </h2>
          <p className="iris-meta">Per-category notification preferences for your account</p>
        </div>
        {isDirty && (
          <button
            type="button"
            className="iris-btn iris-btn--primary"
            disabled={updateSubscriptions.isPending}
            onClick={handleSave}
          >
            {updateSubscriptions.isPending ? "Saving\u2026" : "Save Changes"}
          </button>
        )}
      </div>

      {isLoading ? (
        <p style={{ fontSize: 12, color: "var(--iris-fg-3)" }}>Loading\u2026</p>
      ) : (
        <div className="iris-card" style={{ overflow: "hidden", padding: 0 }}>
          {/* Header */}
          <div
            className="grid grid-cols-[2fr_80px_140px_140px] gap-2"
            style={{
              borderBottom: "1px solid var(--iris-line-1)",
              padding: "12px 18px",
              fontSize: 11,
              color: "var(--iris-fg-4)",
            }}
          >
            <span>Category</span>
            <span>Subscribed</span>
            <span>Channel</span>
            <span>Delivery</span>
          </div>

          {(subscriptions ?? []).map((sub: UserSubscription) => {
            const subscribed = getField(sub, "subscribed") as boolean;
            const channelPref = (getField(sub, "channel_preference") as string | null) ?? "";
            const deliveryOverride = (getField(sub, "delivery_override") as string | null) ?? "";
            const cat = (categories ?? []).find((c) => c.id === sub.category);

            return (
              <div
                key={sub.id}
                className="grid grid-cols-[2fr_80px_140px_140px] items-center gap-2"
                style={{
                  borderBottom: "1px solid var(--iris-line-1)",
                  padding: "12px 18px",
                  fontSize: 12,
                }}
              >
                <div className="flex flex-col gap-0.5">
                  <span style={{ color: "var(--iris-fg-1)" }}>{sub.category_name}</span>
                  {cat?.description && <span className="iris-meta">{cat.description}</span>}
                </div>

                <div className="flex justify-start">
                  <input
                    type="checkbox"
                    checked={subscribed}
                    onChange={(e) => patch(sub.id, { subscribed: e.target.checked })}
                    className="h-4 w-4 cursor-pointer"
                    style={{ accentColor: "var(--iris-neon-purple)" }}
                  />
                </div>

                <select
                  value={channelPref}
                  onChange={(e) => patch(sub.id, { channel_preference: e.target.value || null })}
                  disabled={!subscribed}
                  className={`${selectCls} disabled:opacity-40`}
                >
                  <option value="">Default</option>
                  <option value="email">Email</option>
                  <option value="slack_webhook">Slack</option>
                  <option value="in_app">In-App</option>
                </select>

                <select
                  value={deliveryOverride}
                  onChange={(e) => patch(sub.id, { delivery_override: e.target.value || null })}
                  disabled={!subscribed}
                  className={`${selectCls} disabled:opacity-40`}
                >
                  <option value="">Default</option>
                  <option value="realtime">Realtime</option>
                  <option value="digest">Digest</option>
                </select>
              </div>
            );
          })}

          {(subscriptions ?? []).length === 0 && (
            <div style={{ padding: "16px 18px", fontSize: 12, color: "var(--iris-fg-3)" }}>
              No subscription preferences found
            </div>
          )}
        </div>
      )}
    </section>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────

export function NotificationSettings() {
  return (
    <div className="flex flex-col gap-8">
      <ChannelsSection />
      <RolesSection />
      <PreferencesSection />
    </div>
  );
}
